import React, { useContext, useState, useEffect } from 'react'
import axios from 'axios'
import { M_Table, M_MessageBoard, M_Button, LoadingContext, BufferService, NavigateContext } from 'src/M_Components'
import { useTranslation } from 'react-i18next'
import { ContentCopy, History, Visibility } from '@mui/icons-material'

export const QuoteList = ({ roles, tablesOrder = [], onTablesOrderUpdate }) => {
    const [refresh, setRefresh] = useState(false)
    const [selectedRows, setSelectedRows] = useState([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [quoteToDelete, setQuoteToDelete] = useState()
    const [openRevisionHistory, setOpenRevisionHistory] = useState(false)
    const [revisionHistoryRows, setRevisionHistoryRows] = useState([])
    const [revisionHistoryTitle, setRevisionHistoryTitle] = useState('')
    const bufferServiceQuote = new BufferService('Quote')
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const { t } = useTranslation()

    const roleName =
        roles.find(r => /^quoterequest/i.test(r))
            ? 'quoteRequest'
            : roles.find(r => /^quote/i.test(r))
                ? 'Quote'
                : '';

    const isNaipQuotePanel = roleName === 'Quote'

    useEffect(() => {
        setRefresh(true)
    }, [])

    const NewRoute = (url, label, operation, data) => {
        const tempPerm = []

        roles.forEach((role) => {
            if (role.match('Quote') && !role.toLowerCase().startsWith('quoterequest')) {
                tempPerm.push(role.split('Quote')[1])
            } else if (role.toLowerCase().startsWith('quoterequest')) {
                tempPerm.push(role.split('quoteRequest')[1] ?? role.split('QuoteRequest')[1])
            }
        })

        // onlyRequests = true apenas se NÃO existir nenhuma role de Quote puro
        // roles.some() garante que não depende da ordem das roles no array
        const onlyRequests = !roles.some(r => r.match('Quote') && !r.toLowerCase().startsWith('quoterequest'))

        // Garantir que sempre temos linhas
        const selectedLines = selectedRows && selectedRows.length > 0
            ? selectedRows
            : (data ? [data] : [])

        ctx.passValue?.set('operation', operation)

        if (operation === 'create') {
            onlyRequests
                ? ctx2.newTab('go/quoteRequest', t("RequestQuote"))
                : ctx2.newTab('go/quoteDetail', t("CreateQuote"))
            return
        }

        // VALIDAR seleção
        if (!data && selectedLines.length === 0) {
            ctx.toastMessages('error', '', t("SelectedLine"))
            return
        }

        // COPY — sempre vai para quoteDetail
        if (operation === 'copyHeader' || operation === 'copyHeaderAndCharges') {
            ctx.passValue.set('Row', data)
            ctx.passValue.set('Rows', selectedLines)
            ctx2.newTab('go/quoteDetail', t("CopyQuote"))
            return
        }

        // EDIT
        if (operation === 'edit') {
            ctx.passValue.set('Row', data)
            ctx.passValue.set('Rows', selectedLines)
            onlyRequests
                ? ctx2.newTab('go/quoteRequest', t("EditRequestQuote"))
                : ctx2.newTab('go/quoteDetail', t("EditQuote"))
            return
        }
    }

    const openRevisionHistoryDialog = async (data) => {
        const row = Array.isArray(data) ? data[data.length - 1] : data
        if (!row?.Id) {
            ctx.toastMessages('error', '', t("SelectedLine"))
            return
        }

        ctx.setIsLoad(true)
        try {
            const response = await axios.get(
                `${bufferServiceQuote.url}${bufferServiceQuote.entityName}/${row.Id}/revisions`
            )
            if (response?.data?.name === 'AxiosError') {
                ctx.toastMessages('error', '', t("ErrorLoadingRevisionHistory"))
                return
            }
            setRevisionHistoryRows(response.data || [])
            setRevisionHistoryTitle(`${t("RevisionHistory")} — ${row.QuoteCode || row.quoteCode || ''}`)
            setOpenRevisionHistory(true)
        } catch {
            ctx.toastMessages('error', '', t("ErrorLoadingRevisionHistory"))
        } finally {
            ctx.setIsLoad(false)
        }
    }

    const deleteQuote = (data) => {
        const tempData = data
        ctx.passValue.set('lines', tempData)
        if (!tempData) {
            ctx.toastMessages('error', t("SelectedLine"), '')
            return
        }
        if (Array.isArray(tempData)) {
            ctx.passValue.delete('lines')
            deleteFunc(tempData[tempData.length - 1].Id)
            return
        }
        deleteFunc(tempData.Id)
    }

    const getExtraButtons = () => {
        const tempButtons = []
        const tempPerm = []

        roles.forEach((role) => {
            if (role.match('Quote')) {
                tempPerm.push(role.split('Quote')[1])
            }
        })

        if (tempPerm.includes('Create')) {
            tempButtons.push({ label: t('CopyHeader'), element: 'default', icon: <ContentCopy />, command: (data) => { NewRoute('', '', 'copyHeader', data) } })
            tempButtons.push({ label: t('CopyHeaderAndCharges'), element: 'default', icon: <ContentCopy />, command: (data) => { NewRoute('', '', 'copyHeaderAndCharges', data) } })
        }

        if (isNaipQuotePanel) {
            tempButtons.push({
                label: t('RevisionHistory'),
                element: 'default',
                icon: <History />,
                command: (data) => { openRevisionHistoryDialog(data) },
            })
        }

        return tempButtons
    }

    function deleteFunc(id) {
        bufferServiceQuote.delete(id).then(ResData => {
            if (ResData.name === 'AxiosError') {
                ctx.toastMessages('error', '', t("ErrorQuote"))
                return
            }
            ctx.toastMessages('success', '', t("SuccessQuoteDeleted"))
            setRefresh(true)
            setOpenDeleteDialog(false)
        })
    }

    const removeContextItems = ['View', 'Copy']

    const openRevisionView = (revisionRow) => {
        const revisionId = revisionRow.id || revisionRow.Id
        const rev = revisionRow.revision ?? revisionRow.Revision
        if (!revisionId) return

        const sorted = [...revisionHistoryRows].sort(
            (a, b) => (b.revision ?? b.Revision) - (a.revision ?? a.Revision),
        )
        const currentIndex = sorted.findIndex((r) => (r.id || r.Id) === revisionId)
        const previous = sorted[currentIndex + 1]

        ctx.passValue.set('Row', { Id: revisionId })
        if (previous) {
            ctx.passValue.set('PreviousRevisionRow', { Id: previous.id || previous.Id })
        } else {
            ctx.passValue.delete('PreviousRevisionRow')
        }

        ctx2.newTab('go/quoteRevisionDetail', `${t('ViewRevision')} ${rev}`)
        setOpenRevisionHistory(false)
    }

    const formatRevisionDate = (value) => {
        if (!value) return ''
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
    }

    return (
        <div className='backgroundForms'>
            < div >
                <div>
                    <M_Table
                        roleName={roleName}
                        filtersOnTable
                        entityName={'quote'}
                        tablesOrder={tablesOrder}
                        onTablesOrderUpdate={onTablesOrderUpdate}
                        removeContextItems={removeContextItems}
                        refresh={refresh}
                        setRefresh={setRefresh}
                        rolePermissions={roles}
                        replaceFunctions={new Map([
                            ['Delete', (data) => {
                                setQuoteToDelete(data)
                                setOpenDeleteDialog(true)
                                //deleteQuote(data)
                            }],
                            ['Create', (data) => NewRoute('', '', 'create', data)],
                            ['Edit', (data) => NewRoute('', '', 'edit', data)]
                        ])}
                        onPassLine={(rows) => {
                            setSelectedRows(rows)
                            // ctx.passValue.set('Rows', rows)
                        }}
                        extraButtons={getExtraButtons()}
                    />
                </div>
                <div className='dividerTables'></div>
            </div>
            <M_MessageBoard
                warning
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                title={`${t("Delete")} ${t("Quote")}?`}
                body={<span>
                    {t("TitleConfirmDelete")} <b>{t("Quote")}</b>
                </span>}
                footer={
                    <div style={{ display: 'flex' }}>
                        <M_Button
                            normal
                            hoverEffect
                            label={t("ButtonCancel")}
                            backgroundColor={'var(--color-red)'}
                            labelColor={'var(--color-white)'}
                            onClick={() => setOpenDeleteDialog(false)}
                        />
                        <M_Button
                            normal
                            hoverEffect
                            label={t("Delete")}
                            backgroundColor={'var(--color-green)'}
                            labelColor={'var(--color-white)'}
                            onClick={() => deleteQuote(quoteToDelete)}
                        />
                    </div>
                }
            />
            <M_MessageBoard
                open={openRevisionHistory}
                onClose={() => setOpenRevisionHistory(false)}
                title={revisionHistoryTitle}
                body={
                    revisionHistoryRows.length === 0 ? (
                        <span>{t("NoRevisionHistory")}</span>
                    ) : (
                        <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{t("Revision")}</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{t("CreatedOn")}</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{t("CreatedBy")}</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{t("LastUpdate")}</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{t("LastUpdateBy")}</th>
                                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {revisionHistoryRows.map((row) => (
                                        <tr key={row.id || row.Id}>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                {row.revision ?? row.Revision}
                                                {(row.isCurrent || row.IsCurrent) ? ` (${t("Current")})` : ''}
                                            </td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{formatRevisionDate(row.createdOn || row.CreatedOn)}</td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.createdBy || row.CreatedBy}</td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{formatRevisionDate(row.lastUpdate || row.LastUpdate)}</td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.lastUpdateBy || row.LastUpdateBy}</td>
                                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                                <M_Button
                                                    none
                                                    tooltip={t('ViewRevision')}
                                                    icon={<Visibility />}
                                                    onClick={() => openRevisionView(row)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                }
                footer={
                    <M_Button
                        normal
                        hoverEffect
                        label={t("ButtonCancel")}
                        backgroundColor={'var(--color-green)'}
                        labelColor={'var(--color-white)'}
                        onClick={() => setOpenRevisionHistory(false)}
                    />
                }
            />
        </div >
    )
}
